import { DPM_STATUS, LANG_EN, NOT_PUBLISHED, REQUEST_STATUS, REQUEST_STATUS_OPTIONS, REQUEST_TYPE, REQUEST_TYPE_EXPORT, TITLE_OPTIONS } from "@/app/const";
import { complaintService, userService } from "./serviceProvider";
import { ComplaintDepartmentInfo, ComplaintInfo } from "@/interface/Complaint.interface";
import * as XLSX from 'xlsx';
import { SelectItemType } from "@/app/common/types/selectItem.type";
import { FormStepItem } from "@/interface/Form.interface";
import { PassengerInfo } from "@/interface/Passenger.interface";
import { TicketChannel } from "@/interface/TicketChannel.interface";

interface ExportProgress {
    current: number;
    total: number;
    percentage: number;
}

type ProgressCallback = (progress: ExportProgress) => void;

export default class ReportService {
    
    private requestStatusOptions = REQUEST_STATUS_OPTIONS.filter(o =>
        o.value !== REQUEST_STATUS.DRAFT
    ) as SelectItemType[];

    private requestTypeOptions = REQUEST_TYPE_EXPORT as SelectItemType[];
    private titleOptions = TITLE_OPTIONS;

    // set from caller
    private i18n: any;
    private lang = undefined as string | undefined;
    private formStepComplaint = [] as FormStepItem[];
    private channelOptions = [] as TicketChannel[];
    
    // Performance optimization caches
    private lookupCache = new Map<string, any>();
    private readonly CHUNK_SIZE = 1000; // Process 1000 records at a time

    // Legacy synchronous method - kept for backward compatibility
    exportToExcel(headersExcel: any[], requests: ComplaintInfo[]) {
        this.exportToExcelAsync(headersExcel, requests);
    }

    // Optimized asynchronous export with progress tracking
    async exportToExcelAsync(
        headersExcel: any[], 
        requests: ComplaintInfo[], 
        onProgress?: ProgressCallback
    ): Promise<void> {
        try {
            // Clear cache for fresh export
            this.clearCache();
            
            // Pre-build lookup caches
            this.buildLookupCaches();
            
            const customHeaders = headersExcel.map(header => header.title);
            const totalRecords = requests.length;
            
            // Process data in chunks to avoid blocking UI
            const processedData: any[][] = [];
            
            for (let i = 0; i < totalRecords; i += this.CHUNK_SIZE) {
                const chunk = requests.slice(i, i + this.CHUNK_SIZE);
                const processedChunk = await this.processChunk(chunk, headersExcel);
                processedData.push(...processedChunk);
                
                // Report progress
                if (onProgress) {
                    const current = Math.min(i + this.CHUNK_SIZE, totalRecords);
                    onProgress({
                        current,
                        total: totalRecords,
                        percentage: Math.round((current / totalRecords) * 100)
                    });
                }
                
                // Yield control to prevent UI blocking
                await this.sleep(1);
            }
            
            // Generate Excel file
            await this.generateExcelFile(customHeaders, processedData);
            
        } catch (error) {
            console.error('Excel export failed:', error);
            throw error;
        }
    }
    
    private async processChunk(chunk: ComplaintInfo[], headersExcel: any[]): Promise<any[][]> {
        return chunk.map(item => {
            // Optimize department processing - compute once per item
            const allDpm = this.getAllDepartments(item);
            
            return headersExcel.map(header => {
                return this.processHeaderValue(header, item, allDpm);
            });
        });
    }
    
    private processHeaderValue(header: any, item: ComplaintInfo, allDpm: ComplaintDepartmentInfo[]): any {
        const key = header.key;
        
        // Use switch for better performance than if-else chain
        switch (key) {
            case 'reqType':
                return item.requestType ? this.getCachedTranslation(`reqType-${item.requestType}`, () => 
                    this.i18n.t(`t-${this.requestTypeOptions.find(o => o.value === item.requestType)?.label}`)
                ) : '';
                
            case 'subTopic':
                return this.subTopicName(item);
                
            case 'claims':
                return item.detail?.claims?.map(o => this.claimLabel(o.claimsNo)).join(", ") || '';
                
            case 'detail':
                return item.requestType === REQUEST_TYPE.OCPB 
                    ? item.ocpbInfo?.complaintDetail 
                    : item.requestType === REQUEST_TYPE.OPM ? 
                        item.opmInfo?.detail :item.detail?.detail;
                
            case 'complainant':
                return this.submitterFullName(item);
                
            case 'passengers':
                return item.passengers?.map(o => this.passengerFullName(o)).join(", ") || '';
                
            case 'flightNo':
                return item.flight?.flightNo || '';
                
            case 'departureDate':
                return this.dateString(item.flight?.departureDate);
                
            case 'bookingNos':
                return item.flight?.bookings?.map(o => o.bookingNo).join(", ") || '';
                
            case 'ticketChannels':
                return item.flight?.channel?.mainTicketChannelId ? 
                    this.channelLabel(item.flight.channel.mainTicketChannelId) : '';
                
            case 'relatedMainDepartmentStr':
                return item.relatedMainDepartment?.departmentCode || '';
                
            case 'relatedSubDepartmentStr':
                return item.relatedSubDepartments?.map(o => o.departmentCode).join(", ") || '';
                
            case 'responsiblePersons':
                return this.responsiblePerson(item);
                
            case 'externalDepartments':
                return this.externalDepts(item);
                
            case 'submittedDate':
                return this.dateString(item.submittedDate);
                
            case 'updatedDate':
                return this.dateString(item.updatedDate);
                
            case 'endDate':
                return this.dateString(item.endDate);
                
            case 'status':
                return this.getCachedTranslation(`status-${item.status}`, () => 
                    this.i18n.t(`t-${this.requestStatusOptions.find(o => o.value === item.status)?.label}`)
                );
                
            // Department status cases - optimized with single filter operation
            case 'dpm-inprog':
                return this.filterDepartmentsByStatus(allDpm, DPM_STATUS.IN_PROGRESS);
            case 'dpm-waiting-for-asc-consider':
                return this.filterDepartmentsByStatus(allDpm, DPM_STATUS.ASC_CONSIDER);
            case 'dpm-request-for-additional-documents':
                return this.filterDepartmentsByStatus(allDpm, DPM_STATUS.REQUEST_DOC);
            case 'dpm-waiting-for-additional-documents-tobe-sent':
                return this.filterDepartmentsByStatus(allDpm, DPM_STATUS.UPLOADED);
            case 'dpm-end':
                return this.filterDepartmentsByStatus(allDpm, DPM_STATUS.END);
            case 'dpm-return':
                return this.filterDepartmentsByStatus(allDpm, DPM_STATUS.RETURN);
            case 'dpm-wait-airline':
                return this.filterDepartmentsByStatus(allDpm, DPM_STATUS.WAIT_AIRLINE);
            case 'dpm-accepted':
                return this.filterDepartmentsByStatus(allDpm, DPM_STATUS.ACCEPTED);
                
            default:
                return (item as any)[key] || '';
        }
    }

    setLanguage(lang: string, i18n: any) {
        this.lang = lang;
        this.i18n = i18n;
    }

    setFormStepComplaint(formStep: FormStepItem[]) {
        this.formStepComplaint = formStep;
    }

    setTicketChannel(items: TicketChannel[]) {
        this.channelOptions = items;
    }

    // Performance optimization helper methods
    private clearCache(): void {
        this.lookupCache.clear();
    }

    private buildLookupCaches(): void {
        // Pre-cache frequently used lookups
        this.requestTypeOptions.forEach(option => {
            const key = `reqType-${option.value}`;
            this.lookupCache.set(key, this.i18n.t(`t-${option.label}`));
        });

        this.requestStatusOptions.forEach(option => {
            const key = `status-${option.value}`;
            this.lookupCache.set(key, this.i18n.t(`t-${option.label}`));
        });
    }

    private getCachedTranslation(key: string, fallback: () => string): string {
        if (this.lookupCache.has(key)) {
            return this.lookupCache.get(key);
        }
        const value = fallback();
        this.lookupCache.set(key, value);
        return value;
    }

    private getAllDepartments(item: ComplaintInfo): ComplaintDepartmentInfo[] {
        const allDpm: ComplaintDepartmentInfo[] = [];
        if (item.relatedMainDepartment) {
            allDpm.push(item.relatedMainDepartment);
            if (item.relatedSubDepartments?.length) {
                allDpm.push(...item.relatedSubDepartments);
            }
        }
        return allDpm;
    }

    private filterDepartmentsByStatus(departments: ComplaintDepartmentInfo[], status: string): string {
        return departments
            .filter(dept => dept.departmentStatus === status)
            .map(dept => dept.departmentCode)
            .join(", ");
    }

    private async sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private async generateExcelFile(headers: string[], data: any[][]): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                // Use setTimeout to make Excel generation non-blocking
                setTimeout(() => {
                    try {
                        const exportData = [headers, ...data];
                        const ws = XLSX.utils.aoa_to_sheet(exportData);
                        const wb = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
                        XLSX.writeFile(wb, 'complaint-data.xlsx');
                        resolve();
                    } catch (error) {
                        reject(error);
                    }
                }, 0);
            } catch (error) {
                reject(error);
            }
        });
    }

    private dateString(date: Date | undefined) {
        return complaintService.dateTimeString(date);
    }

    private subTopicName(item: ComplaintInfo) {
        if (this.lang === LANG_EN)
            return item.subTopicEn;
        return item.subTopicTh;
    }

    private claimLabel(claim: number) {
        if (this.formStepComplaint.find(o => o.id === claim))
            return this.i18n.t(`t-${this.formStepComplaint.find(o => o.id === claim)?.name}`)
        return '';
    }

    private hidePersonalInfo(passenger: PassengerInfo) {
        return !passenger.published && userService.isAirline();
    }

    private submitterFullName(item: ComplaintInfo) {
        let personalInfo = item.passengers?.find(o => o.revealInfo);
        if (personalInfo && this.hidePersonalInfo(personalInfo)) {
            return NOT_PUBLISHED;
        }
        const title = this.titleOptions.find(o => o.value === item.submitterTitle)?.label;
        const fullName = `${title ? this.i18n.t('t-' + title) : ''} ${item.submitterFirstName ?? ''} ${item.submitterLastName ?? ''}`;
        return fullName.trim();
    }

    private passengerFullName(personalInfo: PassengerInfo){
        if (personalInfo && this.hidePersonalInfo(personalInfo)) {
            return NOT_PUBLISHED;
        }
        const title = this.titleOptions.find(o => o.value === personalInfo.title)?.label;
        const fullName = `${title ? this.i18n.t('t-' + title) : ''} ${personalInfo.firstName ?? ''} ${personalInfo.lastName ?? ''}`;
        return fullName.trim();
    }

    private channelLabel(id: number) {
        let channel = this.channelOptions.find(o => o.id === id);
        return this.lang === LANG_EN ? channel?.channelNameEn : channel?.channelNameTh;
    }

    private responsiblePerson(item: ComplaintInfo){
        let users = [] as string[];
        if (item.relatedMainDepartment?.responiblePersons) {
            let activeMain = item.relatedMainDepartment?.responiblePersons[0];
            if (activeMain) {
                const fullName = `${activeMain.title ? this.i18n.t('t-' + activeMain.title) : ''} ${activeMain.firstName ?? ''} ${activeMain.lastName ?? ''}`;
                users.push(`${activeMain.departmentCode}: ${fullName}`);
            }
        }
        if (item.relatedSubDepartments && item.relatedSubDepartments.length > 0) {
            item.relatedSubDepartments?.forEach(sub => {
                if (sub.responiblePersons) {
                    let activeSub = sub.responiblePersons[0];
                    if (activeSub) {
                        const fullName = `${activeSub.title ? this.i18n.t('t-' + activeSub.title) : ''} ${activeSub.firstName ?? ''} ${activeSub.lastName ?? ''}`;
                        users.push(`${activeSub.departmentCode}: ${fullName}`);
                    }
                }
            })
        }
        return users.join(", ")
    }

    private externalDepts(item: ComplaintInfo){
        let airlines = [];
        if (item.relatedMainDepartment?.externalDepartment) {
            airlines.push(`${item.relatedMainDepartment.departmentCode}: ${item.relatedMainDepartment.externalDepartmentName}`);
        }
        if (item.relatedSubDepartments && item.relatedSubDepartments.length > 0){
            item.relatedSubDepartments?.forEach(sub => {
                if (sub.external) {
                    airlines.push(`${sub.departmentCode}: ${sub.external.externalDepartmentName}`);
                }
            })
        }
        return airlines.join(", ")
    }
}